// ============================================================
// PERRY — ENEMY BODIES
// THE GORGEOUS PASS, 2026-07-27. Per `ie-being-models`.
//
// Two rigs now, one driver:
//   HUSK    — what the pyre keeps. Ember-orange, ribbed, ruined,
//             and carrying whatever it grabbed when the fire came.
//   WARDEN  — Th'rakk, the Ridge Gate's last warden. Cold, tall,
//             cowled, collared, and holding the Cinder Haul.
//
// The rules that did not change, because they are laws:
//   • No boxes. Spheres, capsules, cones, torus arcs.
//   • Painted cached canvas coats + painted eye discs. Never a
//     flat colour, never eye GEOMETRY.
//   • A `BackSide` ink contour so primitives read as DRAWN.
//   • Fire is GASEOUS — overlapping translucent billows with
//     depthWrite:false, never one hard cone.
//   • ZERO lights. Every glow is emissive or additive. The stage
//     owns exactly two permanently-mounted foe lights and that
//     count never moves.
//   • Everything continuous is a ref read in ONE useFrame.
//
// The state entry the director writes is now:
//   { phase, t, angle, atk, prog, lane, path, body, fr, actor }
// `body` is the Exception Law on the wire — the rig asks what this
// strike wants it to DO ("swing" · "kick" · "cast" · "plant"),
// never which attack id it is. A new attack that says "kick"
// kicks, and this file never learns its name.
// ============================================================

import { useRef, useMemo } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { ENEMIES, GUARD } from "./perryData";
import { mulberry32 } from "./qteEngine";
import FoeWeapon, { ChainLinks, weaponOf } from "./FoeWeapons";

const INK = "#0c0806";

// ── CHARRED HIDE — cracked coal, ash crust, ember veins ──────
const CHAR_CACHE = new Map();
function charTexture(tint) {
  if (CHAR_CACHE.has(tint)) return CHAR_CACHE.get(tint);
  const c = document.createElement("canvas");
  c.width = 512; c.height = 512;
  const g = c.getContext("2d");
  const R = mulberry32(tint.length * 7717 + 313);

  g.fillStyle = "#191110"; g.fillRect(0, 0, 512, 512);

  // soot mottle — the ground tone
  for (let i = 0; i < 240; i++) {
    g.globalAlpha = 0.06 + R() * 0.1;
    g.fillStyle = R() > 0.4 ? "#000000" : "#2e2320";
    g.beginPath(); g.arc(R() * 512, R() * 512, 4 + R() * 16, 0, Math.PI * 2); g.fill();
  }

  // ASH CRUST — pale flaked plates lifting off the char. This is
  // the pass that made the hide read as a SURFACE instead of a
  // noise field: hard-edged plates with a lit rim.
  for (let i = 0; i < 90; i++) {
    const x = R() * 512, y = R() * 512, r = 9 + R() * 26;
    g.beginPath();
    const n = 5 + Math.floor(R() * 3);
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + R() * 0.4;
      const rr = r * (0.6 + R() * 0.6);
      const px = x + Math.cos(a) * rr, py = y + Math.sin(a) * rr;
      if (k === 0) g.moveTo(px, py); else g.lineTo(px, py);
    }
    g.closePath();
    g.globalAlpha = 0.1 + R() * 0.16;
    g.fillStyle = R() > 0.5 ? "#4a423c" : "#241d1a";
    g.fill();
    g.globalAlpha = 0.14 + R() * 0.16;
    g.strokeStyle = "#6a6058"; g.lineWidth = 1.2;
    g.stroke();
  }

  // EMBER VEINS — branching cracks that still hold heat
  g.globalAlpha = 1;
  const branch = (x, y, ang, len, w, depth) => {
    if (depth > 4 || len < 5) return;
    const nx = x + Math.cos(ang) * len;
    const ny = y + Math.sin(ang) * len;
    g.strokeStyle = depth < 2 ? tint : "#ffd6a0";
    g.globalAlpha = 0.85 - depth * 0.12;
    g.lineWidth = w; g.lineCap = "round";
    g.beginPath(); g.moveTo(x, y); g.lineTo(nx, ny); g.stroke();
    branch(nx, ny, ang + (R() - 0.5) * 1.3, len * 0.72, w * 0.68, depth + 1);
    if (R() > 0.45) branch(nx, ny, ang + (R() - 0.5) * 1.9, len * 0.6, w * 0.6, depth + 1);
  };
  for (let i = 0; i < 8; i++) branch(R() * 512, R() * 512, R() * Math.PI * 2, 40 + R() * 46, 5, 0);

  // a bloom of heat under the deepest cracks
  for (let i = 0; i < 34; i++) {
    const x = R() * 512, y = R() * 512, rad = 18 + R() * 30;
    const rg = g.createRadialGradient(x, y, 1, x, y, rad);
    rg.addColorStop(0, tint);
    rg.addColorStop(1, "rgba(0,0,0,0)");
    g.globalAlpha = 0.12 + R() * 0.16;
    g.fillStyle = rg;
    g.beginPath(); g.arc(x, y, rad, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  CHAR_CACHE.set(tint, t);
  return t;
}

// ── THE EYES ARE THE FACE. Painted, never geometry. ──────────
// A 128² disc: hot core → iris → burnt rim, a slit pupil, and a
// specular star. Rendered as a circleGeometry inside a dark
// socket, meshBasic + toneMapped false, so it glows across a
// foggy road for zero lights.
const EYE_CACHE = new Map();
function eyeTexture(color) {
  if (EYE_CACHE.has(color)) return EYE_CACHE.get(color);
  const c = document.createElement("canvas");
  c.width = 128; c.height = 128;
  const g = c.getContext("2d");

  const rg = g.createRadialGradient(64, 64, 2, 64, 64, 62);
  rg.addColorStop(0.0, "#ffffff");
  rg.addColorStop(0.22, "#fff2d2");
  rg.addColorStop(0.48, color);
  rg.addColorStop(0.8, "rgba(90,26,8,0.55)");
  rg.addColorStop(1.0, "rgba(0,0,0,0)");
  g.fillStyle = rg; g.fillRect(0, 0, 128, 128);

  // the slit — a thing that is still looking at you
  g.globalAlpha = 0.82;
  g.fillStyle = "#100604";
  g.beginPath(); g.ellipse(64, 64, 8, 34, 0, 0, Math.PI * 2); g.fill();

  // specular star
  g.globalAlpha = 0.9;
  g.fillStyle = "#ffffff";
  g.beginPath(); g.arc(50, 48, 6, 0, Math.PI * 2); g.fill();
  g.globalAlpha = 1;

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  EYE_CACHE.set(color, t);
  return t;
}

// ── A soft radial billow — one puff of anything gaseous ──────
// NEUTRALISED 2026-07-27. This used to bake an orange fire ramp
// into the texture, which meant every consumer that set a colour
// (the arc, the orb, the sparks, the tell) got that colour
// MULTIPLIED BY ORANGE. Th'rakk burns teal, and teal times orange
// is mud. It is a clean warm-white falloff now, and every
// consumer's own colour finally reads true — including the ones
// that were already asking for it and quietly not getting it.
let BILLOW_TEX = null;
export function billowTexture() {
  if (BILLOW_TEX) return BILLOW_TEX;
  const c = document.createElement("canvas");
  c.width = 128; c.height = 128;
  const g = c.getContext("2d");
  const grad = g.createRadialGradient(64, 64, 2, 64, 64, 62);
  grad.addColorStop(0.0, "rgba(255,255,255,0.98)");
  grad.addColorStop(0.26, "rgba(255,250,240,0.62)");
  grad.addColorStop(0.62, "rgba(255,236,214,0.24)");
  grad.addColorStop(1.0, "rgba(255,230,200,0)");
  g.fillStyle = grad; g.fillRect(0, 0, 128, 128);
  BILLOW_TEX = new THREE.CanvasTexture(c);
  BILLOW_TEX.colorSpace = THREE.SRGBColorSpace;
  return BILLOW_TEX;
}

// ── GASEOUS FIRE — N billows on fixed phases, zero allocation ──
function FireCrown({ n = 8, radius = 0.22, height = 0.5, seed = 1, scale = 1, color = "#ff8a3c" }) {
  const refs = useRef([]);
  const tex = useMemo(() => billowTexture(), []);
  const setup = useMemo(() => {
    const R = mulberry32(seed * 9176 + 7);
    return Array.from({ length: n }, () => ({
      a: R() * Math.PI * 2,
      r: radius * (0.35 + R() * 0.85),
      rate: 0.55 + R() * 0.9,
      off: R() * 6.28,
      size: (0.2 + R() * 0.24) * scale,
    }));
  }, [n, radius, seed, scale]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    setup.forEach((s, i) => {
      const m = refs.current[i];
      if (!m) return;
      const life = (t * s.rate + s.off) % 1;
      m.position.set(
        Math.cos(s.a + life * 1.4) * s.r * (1 - life * 0.4),
        life * height * scale,
        Math.sin(s.a + life * 1.4) * s.r * (1 - life * 0.4)
      );
      const sc = s.size * (0.5 + life * 1.1);
      m.scale.set(sc, sc * 1.35, sc);
      if (m.material) m.material.opacity = (1 - life) * 0.7;
    });
  });

  return (
    <group>
      {setup.map((s, i) => (
        <sprite key={i} ref={(el) => { refs.current[i] = el; }}>
          <spriteMaterial
            map={tex} transparent depthWrite={false} color={color}
            blending={THREE.AdditiveBlending} toneMapped={false} opacity={0.6}
          />
        </sprite>
      ))}
    </group>
  );
}

// ── WHERE A FOE'S HAND IS, in world space ────────────────────
// The stage needs this to anchor a chain that has left the body.
// It is an approximation on purpose: exact bone lookup across a
// component boundary is coupling we do not want for a rope. The
// offsets live here, with the bodies they describe.
export function handPoint(fp, perry, out, h = 1.02) {
  const dx = (perry?.x ?? fp.x + 1) - fp.x;
  const dz = (perry?.z ?? fp.z) - fp.z;
  const len = Math.hypot(dx, dz) || 1;
  out.x = fp.x + (dx / len) * 0.44;
  out.z = fp.z + (dz / len) * 0.44;
  out.y = h;
  return out;
}

// ════════════════════════════════════════════════════════════
// RIG ONE — THE HUSK
// ════════════════════════════════════════════════════════════
// Signature silhouette: a ribcage with the ribs SHOWING (torus
// arcs, the same trick the scythe blade uses), a hollow skull
// with a hanging jaw, arms too long for the body, legs half
// buried in ash — and a tool in the right hand.
function HuskRig({ refs, foe, char, eyeTex, big, weapon }) {
  const { torso, head, armL, armR, legL, legR, weaponGrp } = refs;
  const hide = (
    <meshStandardMaterial
      map={char} roughness={0.94} metalness={0.06}
      emissive={foe.tint} emissiveIntensity={0.38}
    />
  );

  return (
    <group ref={torso} scale={big}>
      {/* ── ribcage: an ovoid, plus real ribs arcing off it ── */}
      <mesh castShadow position={[0, 0.52, 0]} scale={[0.72, 0.92, 0.62]}>
        <sphereGeometry args={[0.3, 16, 13]} />
        {hide}
      </mesh>
      <mesh position={[0, 0.52, 0]} scale={[0.756, 0.966, 0.651]}>
        <sphereGeometry args={[0.3, 16, 13]} />
        <meshBasicMaterial color={INK} side={THREE.BackSide} />
      </mesh>
      {[0.4, 0.5, 0.6, 0.69].map((y, i) => (
        <mesh key={`rib${i}`} position={[0, y, 0.005]} rotation={[Math.PI / 2, 0, 0]} scale={[1, 1, 0.62]}>
          <torusGeometry args={[0.2 - Math.abs(i - 1.4) * 0.022, 0.014, 5, 14, Math.PI * 1.15]} />
          <meshStandardMaterial map={char} roughness={1} emissive={foe.tint} emissiveIntensity={0.5} />
        </mesh>
      ))}
      {/* the coal in the chest — what is actually keeping it up */}
      <mesh position={[0, 0.52, 0.1]}>
        <sphereGeometry args={[0.07, 10, 8]} />
        <meshBasicMaterial color={foe.tint} transparent opacity={0.5} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* hips */}
      <mesh castShadow position={[0, 0.2, 0]} scale={[0.62, 0.5, 0.55]}>
        <sphereGeometry args={[0.26, 13, 11]} />
        {hide}
      </mesh>

      {/* ── head: hollow skull, painted eye lamps, hanging jaw ── */}
      <group ref={head} position={[0, 0.92, 0.02]}>
        <mesh castShadow scale={[0.8, 0.95, 0.8]}>
          <sphereGeometry args={[0.18, 15, 13]} />
          {hide}
        </mesh>
        <mesh scale={[0.848, 1.007, 0.848]}>
          <sphereGeometry args={[0.18, 15, 13]} />
          <meshBasicMaterial color={INK} side={THREE.BackSide} />
        </mesh>
        {[-0.062, 0.062].map((x, i) => (
          <group key={i} position={[x, 0.02, 0.118]} rotation={[0, x * 1.8, 0]}>
            <mesh position={[0, 0, -0.012]} scale={[1, 0.9, 0.5]}>
              <sphereGeometry args={[0.048, 9, 8]} />
              <meshBasicMaterial color="#080503" />
            </mesh>
            <mesh position={[0, 0, 0.006]}>
              <circleGeometry args={[0.04, 14]} />
              <meshBasicMaterial map={eyeTex} transparent depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* brow ridge — it reads as a face even with no nose */}
        <mesh position={[0, 0.075, 0.1]} rotation={[0.3, 0, 0]} scale={[1.15, 0.32, 0.5]}>
          <sphereGeometry args={[0.12, 10, 8]} />
          {hide}
        </mesh>
        {/* jaw hanging open */}
        <mesh position={[0, -0.13, 0.08]} rotation={[0.5, 0, 0]} scale={[0.7, 0.35, 0.6]}>
          <sphereGeometry args={[0.13, 11, 9]} />
          {hide}
        </mesh>
      </group>

      {/* ══════════════════════════════════════════════════════
          ARMS — rebuilt 2026-07-29 as a real joint chain
          ══════════════════════════════════════════════════════
          The old arm was three meshes placed by hand at absolute
          offsets and then EACH rotated on its own, and the two
          decisions never agreed. Measured, it came apart in three
          places: the forearm began 0.36 in front of and 0.10 above
          where the upper arm ended, and the hand floated 0.31 clear
          of the wrist. Worse, every rotation was POSITIVE about X —
          which tips a capsule's lower end BACKWARD — while the
          positions marched forward, so each segment pointed against
          the direction the limb was travelling. That is the wonk.

          Now it is a chain, the way a limb is: every group sits AT
          its joint, and the mesh hangs BELOW the group. Rotating a
          group swings everything under it, which is what a joint
          does. Nothing is placed twice, so nothing can disagree.

            shoulder  y  0.000  z 0.000   ← `r`, the animated ref
            elbow     y -0.387  z 0.103
            wrist     y -0.587  z 0.402

          THE CONTRACT HOLDS. The wrist lands 0.033 / 0.018 off the
          old hand anchor, so `weaponGrp` keeps its exact original
          position and rotation, `handPoint`'s 0.44 reach is still
          right, and every pose in the animation block still drives
          `r.rotation.x/z` at the shoulder exactly as before.

          BOTH hands have fingers now. The old rig gave them only to
          the weapon hand, so a husk's left arm ended in a bare ball
          — and the free hand is the one the whole prologue is about
          ("A hand closes on your ankle"). It gets the CLAW; the
          weapon hand gets the CURL. */}
      {[[-1, armL], [1, armR]].map(([s, r], i) => (
        <group key={i} ref={r} position={[s * 0.27, 0.72, 0]}>
          {/* deltoid — overlaps the ribcage so the arm grows out of
              the body instead of being posted into it (§1: silhouettes
              are overlapping volumes, not joined parts) */}
          <mesh castShadow position={[s * 0.015, -0.005, 0.005]} scale={[1.05, 0.95, 1]}>
            <sphereGeometry args={[0.082, 11, 9]} />
            {hide}
          </mesh>

          {/* ── UPPER ARM ── hangs at -0.26, forward */}
          <group rotation={[-0.26, 0, s * 0.13]}>
            <mesh castShadow position={[0, -0.2, 0]}>
              <capsuleGeometry args={[0.054, 0.292, 4, 9]} />
              {hide}
            </mesh>

            {/* ── ELBOW ── local -0.72, so world hang is -0.98 */}
            <group position={[0, -0.4, 0]} rotation={[-0.72, 0, -s * 0.05]}>
              <mesh castShadow scale={[1, 0.92, 1]}>
                <sphereGeometry args={[0.052, 10, 8]} />
                {hide}
              </mesh>

              {/* ── FOREARM ── thinner than the upper arm; the taper
                  is most of what reads as "arms too long for it" */}
              <mesh castShadow position={[0, -0.18, 0]}>
                <capsuleGeometry args={[0.043, 0.274, 4, 9]} />
                {hide}
              </mesh>

              {/* ── WRIST ── */}
              <group position={[0, -0.36, 0]} rotation={[0.34, 0, 0]}>
                <mesh castShadow position={[0, -0.045, 0.012]} scale={[1.08, 0.72, 1.3]}>
                  <sphereGeometry args={[0.058, 10, 8]} />
                  {hide}
                </mesh>

                {/* four fingers + a thumb, on BOTH hands. The weapon
                    hand curls closed around a haft; the free hand
                    opens into a claw and reaches half a knuckle
                    further, because that is the hand in the story. */}
                {[-0.042, -0.014, 0.014, 0.042].map((fx, k) => {
                  const outer = Math.abs(fx) > 0.03;
                  const curl = s > 0 ? 1.45 : 0.85 + (outer ? 0.16 : 0);
                  const len = outer ? 0.062 : 0.078;
                  return (
                    <group key={k} position={[fx, -0.075, 0.05]} rotation={[curl, 0, fx * 2.2]}>
                      <mesh castShadow position={[0, 0, 0]}>
                        <coneGeometry args={[0.014, len, 5]} />
                        {hide}
                      </mesh>
                    </group>
                  );
                })}
                {/* thumb — set across the palm, which is the single
                    detail that stops a hand reading as a fork */}
                <group position={[s * -0.05, -0.05, 0.035]} rotation={[0.9, 0, s * 1.05]}>
                  <mesh castShadow>
                    <coneGeometry args={[0.015, 0.058, 5]} />
                    {hide}
                  </mesh>
                </group>
              </group>
            </group>
          </group>

          {/* THE WEAPON — unchanged anchor, still parented at the
              shoulder. The wrist now arrives underneath it rather
              than a third of a body-length away. */}
          {s > 0 && (
            <group ref={weaponGrp} position={[0.01, -0.62, 0.42]} rotation={[1.15, 0, -0.2]}>
              <FoeWeapon kind={weapon} hot={foe.tint} />
            </group>
          )}
        </group>
      ))}

      {/* ── LEGS — same treatment. The old pair had the foot sphere
          sitting 0.045 INSIDE the shin capsule rather than at the
          end of it, and no knee at all, so a walking husk's shins
          slid through their own feet. Chained now, and still half
          buried in ash on purpose — the drop puts the sole a little
          under the ground plane, which is what sells "wading out of
          a pyre" instead of "standing on it". */}
      {[[-0.12, legL], [0.12, legR]].map(([x, r], i) => (
        <group key={i} ref={r} position={[x, 0.24, 0]}>
          <mesh castShadow position={[0, -0.01, 0]}>
            <sphereGeometry args={[0.072, 10, 8]} />
            {hide}
          </mesh>
          <group rotation={[-0.06, 0, x * 0.35]}>
            <mesh castShadow position={[0, -0.08, 0]}>
              <capsuleGeometry args={[0.062, 0.036, 4, 8]} />
              {hide}
            </mesh>
            {/* knee */}
            <group position={[0, -0.16, 0]} rotation={[0.15, 0, 0]}>
              <mesh castShadow scale={[1, 0.9, 1]}>
                <sphereGeometry args={[0.056, 9, 8]} />
                {hide}
              </mesh>
              <mesh castShadow position={[0, -0.075, 0]}>
                <capsuleGeometry args={[0.05, 0.05, 4, 8]} />
                {hide}
              </mesh>
              {/* foot, at the END of the shin where a foot goes */}
              <mesh castShadow position={[0, -0.15, 0.035]} rotation={[-0.15, 0, 0]} scale={[1, 0.5, 1.7]}>
                <sphereGeometry args={[0.06, 9, 7]} />
                {hide}
              </mesh>
            </group>
          </group>
        </group>
      ))}

      <FireCrown n={9} radius={0.28} height={0.95} seed={foe.id.length + 3} scale={big} color={foe.tint} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// RIG FOUR — THE ASH HOUND (added 2026-07-29)
// ════════════════════════════════════════════════════════════
// caP'n, on seeing it fight: "the guy was glitched, no weapon and
// white." Nothing was glitched. It was the Ash Hound, rendering
// exactly as authored — `arms: ["none"]` so no weapon, tint
// #8a8078 so pale — wearing a HUSK'S BIPED BODY. A dog standing
// upright with two idle arms hanging off it, in a colour that
// reads as a blank mannequin against black water. Correct data,
// wrong animal, and the eye is right to call that broken.
//
// Its own row already said what it is: "It does not carry
// anything. It IS the thing it hits you with." A creature whose
// whole argument is its body cannot borrow someone else's.
//
// THE REF CONTRACT IS THE TRICK. The animation block drives
// armL/armR and legL/legR, so the front legs take the ARM refs
// and the back legs take the LEG refs. Every pose in the file
// then works unchanged — a lunge that rears a husk's arms rears
// this thing's forequarters instead, which is what a dog does
// when it comes at you. THE EXCEPTION LAW, paid off: one rig
// function and one string, no new animation, no new branch in
// the loop.
//
// Silhouette, three things readable at fight distance:
//   1. LOW AND LONG. The spine runs nearly horizontal at 0.52
//      with the shoulder blades standing proud above it.
//   2. THE HEAD IS OUT FRONT, not on top — carried at the end of
//      a long neck, below the shoulder line, the way a hunting
//      animal carries it.
//   3. DIGITIGRADE. It stands on its toes. That single joint is
//      the whole difference between a dog and a table.
function HoundRig({ refs, foe, char, eyeTex, big }) {
  const { torso, head, armL, armR, legL, legR, weaponGrp } = refs;
  const hide = (
    <meshStandardMaterial
      map={char} roughness={0.92} metalness={0.05}
      emissive={foe.tint} emissiveIntensity={0.3}
    />
  );

  // one leg, built once, mirrored and re-used four times. Front
  // and back differ in the direction the lower joint folds —
  // that opposition is most of what makes a quadruped read.
  const Leg = ({ front }) => (
    <>
      <mesh castShadow position={[0, -0.1, 0]} rotation={[front ? 0.16 : -0.2, 0, 0]}>
        <capsuleGeometry args={[0.048, 0.16, 4, 8]} />
        {hide}
      </mesh>
      <group position={[0, -0.22, front ? 0.03 : -0.04]} rotation={[front ? -0.3 : 0.46, 0, 0]}>
        <mesh castShadow>
          <sphereGeometry args={[0.042, 8, 7]} />
          {hide}
        </mesh>
        <mesh castShadow position={[0, -0.1, 0]}>
          <capsuleGeometry args={[0.034, 0.15, 4, 8]} />
          {hide}
        </mesh>
        {/* the toe joint — it stands on this, not on a foot */}
        <group position={[0, -0.21, 0]} rotation={[front ? 0.38 : -0.32, 0, 0]}>
          <mesh castShadow position={[0, -0.04, 0.02]} scale={[1, 0.62, 1.5]}>
            <sphereGeometry args={[0.046, 8, 7]} />
            {hide}
          </mesh>
        </group>
      </group>
    </>
  );

  return (
    <group ref={torso} scale={big}>
      {/* ── the barrel — one long loaf, deeper at the chest ── */}
      <mesh castShadow position={[0, 0.52, 0.06]} scale={[0.62, 0.66, 1.34]}>
        <sphereGeometry args={[0.3, 15, 12]} />
        {hide}
      </mesh>
      {/* chest, hung low and forward of the front legs */}
      <mesh castShadow position={[0, 0.46, 0.3]} scale={[0.6, 0.72, 0.72]}>
        <sphereGeometry args={[0.27, 13, 11]} />
        {hide}
      </mesh>
      {/* haunches, the widest thing on it */}
      <mesh castShadow position={[0, 0.5, -0.3]} scale={[0.74, 0.76, 0.78]}>
        <sphereGeometry args={[0.27, 13, 11]} />
        {hide}
      </mesh>
      {/* shoulder blades standing proud of the spine — the detail
          that stops a quadruped reading as a sausage on posts */}
      {[-1, 1].map((s) => (
        <mesh key={s} castShadow position={[s * 0.14, 0.68, 0.2]} rotation={[0.2, 0, s * 0.3]} scale={[0.5, 0.9, 0.7]}>
          <sphereGeometry args={[0.13, 9, 8]} />
          {hide}
        </mesh>
      ))}
      {/* the coal it runs on, same as every husk carries */}
      <mesh position={[0, 0.48, 0.28]}>
        <sphereGeometry args={[0.06, 10, 8]} />
        <meshBasicMaterial color={foe.tint} transparent opacity={0.45} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── neck and head, carried OUT not UP ── */}
      <mesh castShadow position={[0, 0.55, 0.46]} rotation={[1.15, 0, 0]}>
        <capsuleGeometry args={[0.072, 0.2, 4, 9]} />
        {hide}
      </mesh>
      <group ref={head} position={[0, 0.5, 0.66]}>
        <mesh castShadow scale={[0.72, 0.7, 1.05]}>
          <sphereGeometry args={[0.13, 13, 11]} />
          {hide}
        </mesh>
        {/* the muzzle — long, and it is the front of the animal */}
        <mesh castShadow position={[0, -0.025, 0.13]} rotation={[1.55, 0, 0]}>
          <capsuleGeometry args={[0.045, 0.1, 4, 8]} />
          {hide}
        </mesh>
        <mesh castShadow position={[0, -0.055, 0.15]} rotation={[1.5, 0, 0]} scale={[1, 1, 0.7]}>
          <capsuleGeometry args={[0.032, 0.08, 4, 7]} />
          {hide}
        </mesh>
        {/* eyes, set on the SIDES of the skull the way a hunter's
            are, and angled out so both catch the light */}
        {[-0.075, 0.075].map((x, i) => (
          <group key={i} position={[x, 0.03, 0.075]} rotation={[0, x * 5.5, 0]}>
            <mesh position={[0, 0, -0.01]} scale={[1, 0.85, 0.5]}>
              <sphereGeometry args={[0.038, 9, 8]} />
              <meshBasicMaterial color="#080503" />
            </mesh>
            <mesh position={[0, 0, 0.005]}>
              <circleGeometry args={[0.031, 14]} />
              <meshBasicMaterial map={eyeTex} transparent depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — flat back against the skull, which is a whole
            sentence about intent in one rotation */}
        {[-1, 1].map((s) => (
          <mesh key={s} castShadow position={[s * 0.075, 0.09, -0.06]} rotation={[-0.75, 0, s * 0.42]}>
            <coneGeometry args={[0.036, 0.13, 5]} />
            {hide}
          </mesh>
        ))}
      </group>

      {/* ── FRONT LEGS on the ARM refs, BACK LEGS on the LEG refs ──
          This is the whole reason the hound needed no new animation.
          The pivots sit at the shoulder and the hip, so every pose
          the file already knows how to strike still lands. */}
      {[[-1, armL], [1, armR]].map(([s, r], i) => (
        <group key={i} ref={r} position={[s * 0.15, 0.5, 0.26]}>
          <Leg front />
        </group>
      ))}
      {[[-1, legL], [1, legR]].map(([s, r], i) => (
        <group key={i} ref={r} position={[s * 0.17, 0.5, -0.3]}>
          <Leg front={false} />
        </group>
      ))}

      {/* the tail — the house bead-chain primitive, tapering,
          tip hot, so there is something moving at the back of the
          silhouette even when it is standing still */}
      {[0, 1, 2, 3, 4, 5].map((k) => {
        const u = k / 5;
        return (
          <mesh key={k} castShadow position={[0, 0.56 + u * 0.1, -0.52 - u * 0.34]} scale={1 - u * 0.55}>
            <sphereGeometry args={[0.055, 8, 7]} />
            {k === 5
              ? <meshBasicMaterial color={foe.tint} transparent opacity={0.65} toneMapped={false} />
              : hide}
          </mesh>
        );
      })}

      {/* The animation block sets `weaponGrp.visible` unconditionally.
          The hound holds nothing, so this is an empty group that
          exists only to keep that contract true — cheaper and far
          safer than making the loop ask what kind of body it is. */}
      <group ref={weaponGrp} />

      <FireCrown n={7} radius={0.3} height={0.7} seed={foe.id.length + 5} scale={big} color={foe.tint} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// RIG TWO — TH'RAKK, THE RIDGE GATE'S LAST WARDEN
// ════════════════════════════════════════════════════════════
// Signature silhouette, three things, readable at any distance:
//   1. THE COWL. A cone shell over a head with no face in it,
//      only two cold lamps set deep in the dark of the hood.
//   2. THE COLLAR. A closed iron ring ENCIRCLING the neck, with
//      the Haul shackled to it. Per `ie-being-models` §4b the
//      attachment IS the read: a ring around a neck is a collar,
//      strands off a skull are hair. It has to be a ring.
//   3. THE MANTLE. Gaseous — translucent billows with depthWrite
//      off, so the drape melts into one volume of ash-smoke
//      instead of stacking like beads.
// Taller and leaner than a husk, and COLD: every other fire in
// Act I is ember-orange and this one has gone teal.
function WardenRig({ refs, foe, char, eyeTex, weapon }) {
  const { torso, head, armL, armR, legL, legR, weaponGrp, mantle } = refs;
  const hide = (
    <meshStandardMaterial
      map={char} roughness={0.86} metalness={0.14}
      emissive={foe.tint} emissiveIntensity={0.3}
    />
  );
  const cloth = (
    <meshStandardMaterial
      map={char} roughness={1} metalness={0}
      color="#3a3f42" emissive={foe.tint} emissiveIntensity={0.12}
      side={THREE.DoubleSide}
    />
  );
  const billow = useMemo(() => billowTexture(), []);

  return (
    <group ref={torso} scale={1.34}>
      {/* ── long torso: two overlapping ovoids, chest over gut ── */}
      <mesh castShadow position={[0, 0.66, 0]} scale={[0.78, 0.86, 0.6]}>
        <sphereGeometry args={[0.31, 16, 13]} />
        {hide}
      </mesh>
      <mesh position={[0, 0.66, 0]} scale={[0.816, 0.9, 0.63]}>
        <sphereGeometry args={[0.31, 16, 13]} />
        <meshBasicMaterial color={INK} side={THREE.BackSide} />
      </mesh>
      <mesh castShadow position={[0, 0.34, 0]} scale={[0.64, 0.66, 0.55]}>
        <sphereGeometry args={[0.28, 13, 11]} />
        {hide}
      </mesh>
      {/* the cold coal where the work used to be */}
      <mesh position={[0, 0.7, 0.13]}>
        <sphereGeometry args={[0.075, 10, 8]} />
        <meshBasicMaterial color={foe.tint} transparent opacity={0.55} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── THE MANTLE — gaseous drape off the shoulders ── */}
      <group ref={mantle} position={[0, 0.82, -0.06]}>
        {[
          [-0.3, -0.1, -0.1, 0.4], [0.3, -0.1, -0.1, 0.4],
          [-0.17, -0.34, -0.13, 0.46], [0.17, -0.34, -0.13, 0.46],
          [0, -0.2, -0.19, 0.52], [0, -0.52, -0.14, 0.42],
          [-0.34, -0.44, -0.06, 0.34], [0.34, -0.44, -0.06, 0.34],
        ].map((v, i) => (
          <sprite key={i} position={[v[0], v[1], v[2]]} scale={v[3]}>
            <spriteMaterial
              map={billow} color="#2c4a4e" transparent opacity={0.3}
              depthWrite={false} toneMapped={false}
            />
          </sprite>
        ))}
        {/* a real cloth shell under the vapour so it has a body */}
        <mesh castShadow position={[0, -0.26, -0.06]} rotation={[0.12, 0, 0]}>
          <coneGeometry args={[0.4, 0.72, 9, 1, true]} />
          {cloth}
        </mesh>
      </group>

      {/* ── THE COLLAR — a closed ring, and the Haul made fast ── */}
      <mesh castShadow position={[0, 1, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.135, 0.028, 7, 18]} />
        <meshStandardMaterial
          color="#4c5155" metalness={0.85} roughness={0.42}
          emissive={foe.tint} emissiveIntensity={0.22}
        />
      </mesh>
      <mesh position={[0, 1, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.135, 0.04, 6, 16]} />
        <meshBasicMaterial color={foe.tint} transparent opacity={0.16} toneMapped={false} depthWrite={false} />
      </mesh>
      {/* the shackle ring the chain is made fast to */}
      <mesh position={[0.13, 1.02, 0.05]} rotation={[0.4, 0.3, 0]}>
        <torusGeometry args={[0.045, 0.013, 6, 12]} />
        <meshStandardMaterial color="#5a6064" metalness={0.9} roughness={0.35} />
      </mesh>

      {/* ── the head, and the cowl over it ── */}
      <group ref={head} position={[0, 1.14, 0.01]}>
        <mesh castShadow scale={[0.74, 0.9, 0.78]}>
          <sphereGeometry args={[0.17, 15, 13]} />
          {hide}
        </mesh>
        {[-0.055, 0.055].map((x, i) => (
          <group key={i} position={[x, 0.01, 0.112]} rotation={[0, x * 2, 0]}>
            <mesh position={[0, 0, -0.012]} scale={[1, 0.85, 0.5]}>
              <sphereGeometry args={[0.046, 9, 8]} />
              <meshBasicMaterial color="#03070a" />
            </mesh>
            <mesh position={[0, 0, 0.006]}>
              <circleGeometry args={[0.036, 14]} />
              <meshBasicMaterial map={eyeTex} transparent depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* THE COWL — open at the front, deep enough to hold dark */}
        <mesh castShadow position={[0, 0.06, -0.03]} rotation={[-0.18, 0, 0]}>
          <coneGeometry args={[0.24, 0.44, 10, 1, true]} />
          {cloth}
        </mesh>
        <mesh position={[0, 0.02, 0.02]} scale={[1, 1, 0.5]}>
          <sphereGeometry args={[0.16, 10, 8]} />
          <meshBasicMaterial color="#050a0b" transparent opacity={0.72} depthWrite={false} />
        </mesh>
      </group>

      {/* ── arms — long, and the right one does all the work ── */}
      {[[-1, armL], [1, armR]].map(([s, r], i) => (
        <group key={i} ref={r} position={[s * 0.3, 0.9, 0]}>
          <mesh castShadow position={[0, -0.24, 0.05]} rotation={[0.24, 0, s * 0.12]}>
            <capsuleGeometry args={[0.052, 0.4, 4, 9]} />
            {hide}
          </mesh>
          <mesh castShadow position={[0, -0.56, 0.2]} rotation={[0.62, 0, 0]}>
            <capsuleGeometry args={[0.042, 0.36, 4, 9]} />
            {hide}
          </mesh>
          <mesh castShadow position={[0, -0.74, 0.38]} scale={[1, 0.7, 1.2]}>
            <sphereGeometry args={[0.066, 10, 8]} />
            {hide}
          </mesh>
          {s > 0 && (
            <group ref={weaponGrp} position={[0.01, -0.76, 0.4]} rotation={[1, 0, -0.15]}>
              <FoeWeapon kind={weapon} hot={foe.tint} />
            </group>
          )}
        </group>
      ))}

      {/* ── legs: it is still standing at its post ── */}
      {[[-0.14, legL], [0.14, legR]].map(([x, r], i) => (
        <group key={i} ref={r} position={[x, 0.3, 0]}>
          <mesh castShadow position={[0, -0.15, 0]}>
            <capsuleGeometry args={[0.062, 0.3, 4, 8]} />
            {hide}
          </mesh>
          <mesh castShadow position={[0, -0.32, 0.05]} scale={[1, 0.5, 1.6]}>
            <sphereGeometry args={[0.062, 8, 7]} />
            {hide}
          </mesh>
        </group>
      ))}

      {/* cold fire — fewer billows, slower, and it HANGS instead
          of rising, because nothing in this thing is hot any more */}
      <FireCrown n={7} radius={0.3} height={0.55} seed={11} scale={1} color={foe.tint} />

      {/* rim aura — the whole warden is faintly spectral */}
      <mesh position={[0, 0.72, 0]} scale={[1.5, 2.1, 1.4]}>
        <sphereGeometry args={[0.34, 12, 10]} />
        <meshBasicMaterial
          color={foe.tint} side={THREE.BackSide} transparent opacity={0.085}
          depthWrite={false} toneMapped={false}
        />
      </mesh>
    </group>
  );
}


// ════════════════════════════════════════════════════════════
// THE DISCIPLE OF T'YPHIZ — the Champion, and the first thing in
// the game that was never on fire
// ════════════════════════════════════════════════════════════
// Every foe before this one is something the fire HAPPENED TO. A
// husk was a farmhand. Th'rakk was a man with a job. Both of them
// are shaped like ruin — hunched, crusted, leaking.
//
// The Disciple walked in on its own feet. So the entire silhouette
// argues the opposite: it is UPRIGHT, it is TALL, it is
// SYMMETRICAL, and it is dressed. Nothing about it sags. When it
// is standing beside a husk the difference should be legible from
// the back of the frame with the sound off.
//
// THE MASK. Old Maren's line — "a thing with a bill of beaten
// iron" — and the Treasure's lore — "torn off something that WORE
// it in its master's honour" — have always meant the same object,
// and it is not a weapon. It is a FACE. The Disciple has beaten
// itself a copy of T'yphiz's duck bill out of iron and it wears
// it, and when you break the Disciple you take it off and carry it
// (t_bill_of_iron, `guard: 2`, "skin has never impressed it").
//
// It carries a billhook as well, because a Champion is allowed two
// pieces of iron. That one does not drop.
//
// Per ie-being-models: no boxes anywhere, painted eye discs in
// real sockets (never eye geometry), a BackSide ink contour so
// primitives read as DRAWN, and one closed ring of attachment —
// here the pectoral collar the vestment hangs from.
function DiscipleRig({ refs, foe, char, eyeTex, weapon }) {
  const { torso, head, armL, armR, legL, legR, weaponGrp, mantle } = refs;
  const hide = (
    <meshStandardMaterial
      map={char} roughness={0.7} metalness={0.2}
      emissive={foe.tint} emissiveIntensity={0.22}
    />
  );
  // The vestment. Wet at the hem, because it has been standing in
  // a chapel full of fen water for however long this has been.
  const cloth = (
    <meshStandardMaterial
      map={char} roughness={0.96} metalness={0.04}
      color="#2a2130" emissive={foe.tint} emissiveIntensity={0.1}
      side={THREE.DoubleSide}
    />
  );
  const iron = (
    <meshStandardMaterial
      color="#5a5560" metalness={0.88} roughness={0.34}
      emissive={foe.tint} emissiveIntensity={0.18}
    />
  );
  const billow = useMemo(() => billowTexture(), []);

  return (
    <group ref={torso} scale={1.46}>
      {/* ── the frame: a long straight chest over narrow hips.
             Two ovoids like every body in this game, but stacked
             VERTICALLY rather than slumped forward. */}
      <mesh castShadow position={[0, 0.74, 0]} scale={[0.7, 0.94, 0.52]}>
        <sphereGeometry args={[0.31, 16, 14]} />
        {hide}
      </mesh>
      <mesh position={[0, 0.74, 0]} scale={[0.735, 0.985, 0.548]}>
        <sphereGeometry args={[0.31, 16, 14]} />
        <meshBasicMaterial color={INK} side={THREE.BackSide} />
      </mesh>
      <mesh castShadow position={[0, 0.38, 0]} scale={[0.56, 0.6, 0.46]}>
        <sphereGeometry args={[0.28, 13, 11]} />
        {hide}
      </mesh>

      {/* ── THE PECTORAL ── the closed ring of attachment. The
             vestment hangs off THIS, and you can see that it does,
             which is the whole ie-being-models rule about how a
             worn thing reads as worn. */}
      <mesh castShadow position={[0, 1.02, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.15, 0.024, 8, 20]} />
        {iron}
      </mesh>
      <mesh position={[0, 1.02, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.15, 0.038, 6, 16]} />
        <meshBasicMaterial color={foe.tint} transparent opacity={0.14} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── THE VESTMENT ── a long front-and-back tabard. Real
             cloth, cone shells, DoubleSide, and it lags the body
             in the driver — cloth only ever reads as cloth when it
             is late. */}
      <group ref={mantle} position={[0, 0.98, 0]}>
        <mesh castShadow position={[0, -0.44, 0.03]} rotation={[0.05, 0, 0]}>
          <coneGeometry args={[0.33, 1.02, 10, 1, true]} />
          {cloth}
        </mesh>
        <mesh castShadow position={[0, -0.4, -0.05]} rotation={[-0.06, 0, 0]}>
          <coneGeometry args={[0.30, 0.9, 9, 1, true]} />
          {cloth}
        </mesh>
        {/* the waterline — a dark band where the fen has been
            standing against it for a very long time */}
        <mesh position={[0, -0.82, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.29, 0.035, 6, 18]} />
          <meshBasicMaterial color="#0a1418" transparent opacity={0.6} depthWrite={false} />
        </mesh>
        {/* cold vapour off the hem — it is not smoking. It is
            EVAPORATING, slightly, which is a different verb. */}
        {[[-0.2, -0.74, 0.06, 0.3], [0.2, -0.74, 0.06, 0.3],
          [0, -0.86, -0.02, 0.36], [-0.1, -0.9, 0.1, 0.26], [0.12, -0.9, 0.08, 0.26]].map((v, i) => (
          <sprite key={i} position={[v[0], v[1], v[2]]} scale={v[3]}>
            <spriteMaterial
              map={billow} color="#4a3a5e" transparent opacity={0.24}
              depthWrite={false} toneMapped={false}
            />
          </sprite>
        ))}
      </group>

      {/* ══════════════════════════════════════════════════════
          THE HEAD, AND THE BILL OF BEATEN IRON
          ══════════════════════════════════════════════════════
          The mask is a WIDE FLAT BEAK — two shallow half-ovoids,
          upper and lower, hinged at the cheek, exactly the shape
          the village laughed at until it was the size of a door
          and full of light. Nobody is laughing in the chapel. */}
      <group ref={head} position={[0, 1.2, 0.01]}>
        <mesh castShadow scale={[0.66, 0.84, 0.7]}>
          <sphereGeometry args={[0.17, 15, 13]} />
          {hide}
        </mesh>

        {/* sockets, then painted discs. NEVER eye geometry. */}
        {[-0.062, 0.062].map((x, i) => (
          <group key={i} position={[x, 0.045, 0.1]} rotation={[0, x * 2.2, 0]}>
            <mesh position={[0, 0, -0.014]} scale={[1, 0.9, 0.46]}>
              <sphereGeometry args={[0.05, 9, 8]} />
              <meshBasicMaterial color="#05030a" />
            </mesh>
            <mesh position={[0, 0, 0.006]}>
              <circleGeometry args={[0.038, 14]} />
              <meshBasicMaterial map={eyeTex} transparent depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        ))}

        {/* ── THE BILL ── upper mandible */}
        <mesh castShadow position={[0, -0.035, 0.19]} rotation={[0.1, 0, 0]} scale={[1.5, 0.34, 2.0]}>
          <sphereGeometry args={[0.088, 12, 9]} />
          {iron}
        </mesh>
        {/* lower mandible, slightly parted — it is always slightly
            parted, and it never moves, and that is the unsettling
            part */}
        <mesh castShadow position={[0, -0.088, 0.175]} rotation={[0.02, 0, 0]} scale={[1.34, 0.22, 1.72]}>
          <sphereGeometry args={[0.086, 12, 9]} />
          {iron}
        </mesh>
        {/* the hammer-marks: three ribs across the bill, which is
            what "beaten" looks like at this distance */}
        {[0.10, 0.15, 0.20].map((z, i) => (
          <mesh key={i} position={[0, -0.036, z]} rotation={[Math.PI / 2, 0, 0]} scale={[1, 1, 0.22]}>
            <torusGeometry args={[0.062 - i * 0.008, 0.006, 4, 12]} />
            {iron}
          </mesh>
        ))}
        {/* the strap over the crown — a mask is a thing somebody
            put on, and the buckle is how the frame says so */}
        <mesh position={[0, 0.09, 0.02]} rotation={[0, 0, Math.PI / 2]} scale={[1, 1, 0.5]}>
          <torusGeometry args={[0.15, 0.012, 5, 14]} />
          {iron}
        </mesh>

        {/* ── THE HORNS ── iron, strapped on, in its master's
               honour. They are not its horns. They never were. */}
        {[-1, 1].map((sx) => (
          <mesh key={sx} castShadow position={[sx * 0.1, 0.15, -0.02]}
            rotation={[-0.5, 0, sx * 0.42]} scale={[1, 1, 0.75]}>
            <coneGeometry args={[0.032, 0.24, 7]} />
            {iron}
          </mesh>
        ))}
      </group>

      {/* ── arms — long, even, and BOTH of them work. This is the
             thing that chords: two hands, two places, one blow. */}
      {[[-1, armL], [1, armR]].map(([sx, r], i) => (
        <group key={i} ref={r} position={[sx * 0.28, 0.96, 0]}>
          <mesh castShadow position={[0, -0.26, 0.04]} rotation={[0.2, 0, sx * 0.1]}>
            <capsuleGeometry args={[0.048, 0.42, 4, 9]} />
            {hide}
          </mesh>
          <mesh castShadow position={[0, -0.58, 0.18]} rotation={[0.56, 0, 0]}>
            <capsuleGeometry args={[0.038, 0.38, 4, 9]} />
            {hide}
          </mesh>
          {/* a real hand, with a thumb, because a Champion grips */}
          <mesh castShadow position={[0, -0.78, 0.34]} scale={[1, 0.72, 1.15]}>
            <sphereGeometry args={[0.06, 10, 8]} />
            {hide}
          </mesh>
          <mesh castShadow position={[sx * 0.035, -0.76, 0.38]} rotation={[0.5, 0, 0]}>
            <capsuleGeometry args={[0.017, 0.05, 3, 6]} />
            {hide}
          </mesh>
          {sx > 0 && (
            <group ref={weaponGrp} position={[0.01, -0.8, 0.36]} rotation={[1.05, 0, -0.1]}>
              <FoeWeapon kind={weapon} hot={foe.tint} />
            </group>
          )}
        </group>
      ))}

      {/* ── legs: it has been standing here long enough that the
             reeds grew up around it and drowned again */}
      {[[-0.13, legL], [0.13, legR]].map(([x, r], i) => (
        <group key={i} ref={r} position={[x, 0.34, 0]}>
          <mesh castShadow position={[0, -0.18, 0]}>
            <capsuleGeometry args={[0.056, 0.34, 4, 8]} />
            {hide}
          </mesh>
          <mesh castShadow position={[0, -0.37, 0.04]} scale={[1, 0.46, 1.5]}>
            <sphereGeometry args={[0.058, 8, 7]} />
            {hide}
          </mesh>
        </group>
      ))}

      {/* NO FIRE CROWN. That is the entire character note and it
          is expressed by an absence, which is the cheapest and
          best kind of expression there is. What it has instead is
          a cold halo that does not flicker. */}
      <mesh position={[0, 1.24, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.30, 0.008, 4, 28]} />
        <meshBasicMaterial color={foe.tint} transparent opacity={0.3} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0.8, 0]} scale={[1.42, 2.25, 1.3]}>
        <sphereGeometry args={[0.34, 12, 10]} />
        <meshBasicMaterial
          color={foe.tint} side={THREE.BackSide} transparent opacity={0.075}
          depthWrite={false} toneMapped={false}
        />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE DRIVER — one useFrame, every rig
// ════════════════════════════════════════════════════════════
// posArr / stateArr are the DIRECTOR'S ref arrays; we index into
// them every frame rather than holding a wrapper object, because
// the director REPLACES the entries at each encounter start and a
// wrapper would quietly go stale on the second fight.
export default function EnemyBody({ enemyId = "burning_husk", posArr, stateArr, index = 0, perryRef }) {
  const group = useRef();
  const torso = useRef();
  const armL = useRef();
  const armR = useRef();
  const legL = useRef();
  const legR = useRef();
  const head = useRef();
  const glow = useRef();
  const weaponGrp = useRef();
  const mantle = useRef();
  const chainRefs = useRef([]);
  const rig = { torso, head, armL, armR, legL, legR, weaponGrp, mantle };

  const foe = ENEMIES[enemyId] || ENEMIES.burning_husk;
  // ── THE EXCEPTION LAW, on the wire ──
  // This used to be `const isWarden = !!foe.boss`, which decided,
  // from inside a fight nobody had designed yet, that every boss
  // that would ever exist is a chain warden. `rig` is a STRING
  // FIELD: a husk may be given a warden's frame, a Champion may be
  // given a husk's, and the next rig costs a function and a line.
  const rigKind = foe.rig || (foe.boss ? "warden" : "husk");
  const isWarden = rigKind === "warden";
  const isDisciple = rigKind === "disciple";
  const isHound = rigKind === "hound";
  // A hound's back is at 0.52 where a husk's head is at 0.92, so
  // the tell sprite and the glow have to come down with it or they
  // hang in the air above an animal that is not there.
  const tall = isWarden ? 1.3 : isDisciple ? 1.42 : isHound ? 0.66 : 1;
  const char = useMemo(() => charTexture(foe.tint), [foe.tint]);
  const eyeTex = useMemo(
    () => eyeTexture(
      isWarden ? "#8ff4e6" : isDisciple ? "#e0c0ff" : isHound ? "#d8f07a" : "#ffb85a"
    ),
    [isWarden, isDisciple, isHound]
  );
  const weapon = useMemo(() => weaponOf(foe, index), [foe, index]);
  const big = foe.id === "ridge_husk" ? 1.28 : 1;

  // scratch, allocated once, never in the loop
  const v = useRef({ a: new THREE.Vector3(), b: new THREE.Vector3() }).current;

  useFrame((state, dt) => {
    const g = group.current;
    if (!g) return;
    const p = posArr?.current?.[index];
    if (p) g.position.set(p.x, p.y || 0, p.z);

    // It turns to look at Perry every frame, like a thing that
    // wants something from him.
    const pr = perryRef?.current;
    if (pr && p) {
      const wantYaw = Math.atan2(pr.x - p.x, pr.z - p.z);
      let dy = wantYaw - g.rotation.y;
      while (dy > Math.PI) dy -= Math.PI * 2;
      while (dy < -Math.PI) dy += Math.PI * 2;
      g.rotation.y += dy * Math.min(1, dt * 9);
    }

    const st = stateArr?.current?.[index] || { phase: "hidden", t: 0, prog: 0 };
    if (st.phase === "hidden") { g.visible = false; return; }
    g.visible = true;
    const t = state.clock.elapsedTime;
    st.t = (st.t || 0) + dt;

    const prog = st.prog || 0;
    const d = st.angle ? GUARD.DIRS[st.angle] : null;
    const lane = d ? d.lane : 0;        // −1 Perry's left · +1 Perry's right
    const high = d ? (d.tier === "top" ? 1 : d.tier === "low" ? -1 : 0) : 0;
    const kind = st.body || "swing";    // THE EXCEPTION LAW, read here

    // ── THE COIL — one continuous curve, 0 → 1 → follow-through ──
    // 0.00–0.55  rear back (readable, slow, the tell)
    // 0.55–1.00  accelerate through (fast, committed)
    // 1.00+      follow-through and settle
    const coil = prog < 0.55
      ? Math.pow(prog / 0.55, 0.7)                 // eases OUT — you see it early
      : 1 - Math.pow((prog - 0.55) / 0.45, 2.2);   // slams IN — impact at 1
    const through = Math.max(0, prog - 1) * 2.2;

    let lean = 0, reach = 0, rise = 0, tilt = 0, twist = 0, hop = 0;
    let kneeL = 0, kneeR = 0, haul = 0, armed = false, holdWeapon = true;

    switch (st.phase) {
      case "rise":
        rise = Math.min(1, st.t / 0.55);
        break;

      case "wind":
      case "strike": {
        rise = 1;
        armed = true;

        if (kind === "kick") {
          // ── THE FLYING KICK ──
          // It gathers under itself, then LEAVES THE GROUND. The
          // lead knee comes up through the coil and snaps straight
          // into the follow-through; the torso counters backward,
          // the way a real kick has to.
          const gather = Math.min(1, coil * 1.6);
          lean = 0.1 * gather - 0.42 * coil + through * 0.55;
          reach = -0.1 * coil + through * 0.5;
          twist = -lane * 0.3 * coil + lane * 0.22 * through;
          tilt = -high * 0.3 * coil;
          hop = coil * coil * 0.62 + through * 0.1;      // airborne
          const k = coil * 1.5 - through * 1.9;
          if (lane >= 0) { kneeR = k; kneeL = -coil * 0.5; }
          else { kneeL = k; kneeR = -coil * 0.5; }
        } else if (kind === "plant") {
          // ── THE PLANT — Th'rakk's chain throw ──
          // It does not step and it does not chase. It settles its
          // weight, draws the blade back across its own body, and
          // throws PAST you. Then it waits, holding the slack. Then
          // it HAULS — a hard whole-body pull backward, which is
          // the exact frame the return begins on.
          const fr = st.fr || { cast: 0.24, behind: 0.62, yank: 0.62 };
          const draw = Math.min(1, prog / Math.max(0.01, fr.cast));
          const thrown = prog >= fr.cast;
          const after = thrown ? Math.min(1, (prog - fr.cast) * 6) : 0;
          const pull = prog >= fr.yank
            ? Math.min(1, (prog - fr.yank) / Math.max(0.01, (1 - fr.yank) * 0.45))
            : 0;
          haul = pull;
          holdWeapon = !thrown || prog > 1.06;

          lean = -0.3 * draw + 0.34 * after - pull * 0.55;
          reach = -0.55 * draw + 0.9 * after - pull * 1.2;
          twist = -lane * 0.42 * draw + lane * 0.3 * after;
          tilt = -high * 0.18 * pull;
          hop = -pull * 0.1;
          kneeL = pull * 0.35;
          kneeR = -pull * 0.2;
        } else if (kind === "spin") {
          // ── THE ROUNDHOUSE ── caP'n's list, item one.
          // A dog does not swing an arm at you. It puts its whole
          // mass on one axis and comes back around facing you.
          //
          // The read is the WIND-UP TURN: it rotates AWAY from the
          // lane first (a full 1.5 rad of counter-twist), which is
          // both what a real spin has to do and, conveniently, the
          // most legible tell in the game — you can see which way
          // it is loading from the back of the frame.
          const load = Math.min(1, coil * 1.4);
          twist = lane * (-1.5 * load + 4.9 * Math.max(0, coil - 0.45) + through * 1.2);
          lean = -0.12 * load + 0.26 * coil;
          reach = -0.06 * coil + through * 0.34;
          tilt = -high * 0.34 * coil - lane * 0.2 * coil;
          hop = coil * 0.16 + (high > 0 ? coil * 0.22 : 0);
          kneeL = (lane >= 0 ? 1 : -1) * coil * 0.7;
          kneeR = (lane >= 0 ? -1 : 1) * coil * 0.7;
        } else if (kind === "overhead") {
          // ── THE CLEFT ── a real two-handed overhead, and the
          // only pose in the game that is SYMMETRICAL. Both arms
          // go up together, the whole frame rises onto the balls
          // of its feet, and it comes down through the middle of
          // itself. Nothing about it says a side, because the
          // answer is not a side — it is both of them.
          const rear = Math.min(1, coil * 1.25);
          lean = -0.62 * rear + through * 1.05;
          reach = -1.15 * rear + through * 1.5;
          twist = 0;
          tilt = 0;
          hop = rear * 0.24 - Math.max(0, prog - 0.9) * 0.5;
          kneeL = -rear * 0.2 + through * 0.5;
          kneeR = -rear * 0.2 + through * 0.5;
        } else if (kind === "cast") {
          lean = -0.2 - 0.2 * coil;
          reach = -0.4 - 0.5 * coil;
          tilt = high * 0.12 * coil;
        } else {
          // ── SWING — the honest one, and the baseline ──
          // It rears AWAY from where the blow will land, then
          // crosses. Lateral is told by the FEET, so the body
          // spends all of its language on the one thing position
          // cannot say: which tier.
          lean = -0.34 * coil + through * 0.5;
          reach = -0.22 * coil + through * 1.1;
          twist = -lane * 0.55 * coil + lane * 0.35 * through;
          tilt = -high * 0.54 * coil;
          hop = high > 0 ? coil * 0.3 : -coil * 0.16;
          if (st.atk === "slash") { twist *= 1.3; reach -= 0.1 * coil; }
        }
        break;
      }

      case "feint": {
        // a lie: it winds up honestly and then simply... doesn't.
        rise = 1;
        const held = Math.min(1, st.t / 0.45);
        lean = -0.34 * held;
        reach = -0.24 * held;
        twist = -lane * 0.5 * held;
        tilt = Math.sin(st.t * 9) * 0.05 * held;    // it shivers with wanting to
        break;
      }

      case "cast":
        rise = 1; armed = true;
        lean = -0.2 - 0.2 * coil;
        reach = -0.4 - 0.5 * coil;
        tilt = high * 0.12 * coil;
        break;

      case "hurl":
        rise = 1; lean = 0.35; reach = 0.9; twist = lane * 0.4;
        break;

      case "recover": rise = 1; lean = 0.12; reach = 0.2; break;
      case "fracture":
        // wide open. Arms out, head back, everything unlocked.
        rise = 1; lean = -0.55; reach = -1.1; tilt = Math.sin(t * 3) * 0.12;
        break;
      case "break":   rise = 1; lean = -0.7; tilt = 0.5; break;
      case "retreat": rise = 1; lean = -0.2; break;
      default:        rise = 1; break;
    }

    const sway = Math.sin(t * 1.7 + index) * 0.05;
    if (torso.current) {
      torso.current.position.y = -0.55 * (1 - rise) + hop;
      torso.current.rotation.x = lean + sway * 0.3;
      torso.current.rotation.z = tilt + sway;
      torso.current.rotation.y = twist;
      torso.current.visible = rise > 0.02;
    }
    if (head.current) {
      head.current.rotation.z = Math.sin(t * 2.1 + index) * 0.08 + tilt * 0.6;
      head.current.rotation.x = st.phase === "fracture"
        ? -0.6
        : -high * 0.2 * coil - haul * 0.25;
    }
    if (mantle.current) {
      // the drape LAGS the body, which is the only reason cloth
      // ever reads as cloth
      mantle.current.rotation.x = -lean * 0.55 + Math.sin(t * 0.9) * 0.04;
      mantle.current.rotation.z = -tilt * 0.5 + Math.sin(t * 1.3 + 1.1) * 0.03;
    }

    // The striking limb.
    const lead = lane >= 0 ? armR : armL;
    const off = lane >= 0 ? armL : armR;
    if (kind === "plant") {
      // the chain arm is always the right one — that is where the
      // Haul is shackled, and a weapon that swaps hands is a lie
      if (armR.current) {
        armR.current.rotation.x = -0.4 - reach * 1.6 - high * 0.7 * coil;
        armR.current.rotation.z = -0.25 + haul * 0.5;
      }
      if (armL.current) {
        armL.current.rotation.x = -0.35 - reach * 0.5 + Math.sin(t * 1.5) * 0.06;
        armL.current.rotation.z = 0.18 - haul * 0.35;
      }
    } else if (kind === "overhead") {
      // BOTH arms, identically. A two-handed blow whose arms
      // disagree is a two-handed blow the player cannot read as
      // one, and reading it as one is the entire mechanic.
      const swing = -0.5 - reach * 2.2;
      if (armL.current) { armL.current.rotation.x = swing; armL.current.rotation.z = 0.16; }
      if (armR.current) { armR.current.rotation.x = swing; armR.current.rotation.z = -0.16; }
    } else {
      if (lead.current) {
        lead.current.rotation.x = -0.5 - reach * 2.1 - high * 1.15 * coil
          + Math.sin(t * 1.3 + index) * 0.06;
        lead.current.rotation.z = -lane * 0.5 * coil - high * 0.35 * coil;
      }
      if (off.current) {
        off.current.rotation.x = -0.5 - reach * 0.8 + Math.sin(t * 1.5 + index) * 0.08;
        off.current.rotation.z = 0;
      }
    }
    if (legL.current) legL.current.rotation.x = kneeL + Math.sin(t * 1.1 + index) * 0.02;
    if (legR.current) legR.current.rotation.x = kneeR + Math.sin(t * 1.1 + index + 2) * 0.02;

    // the blade is not in its hand while it is in the air
    if (weaponGrp.current) weaponGrp.current.visible = holdWeapon;

    // The limb that is about to arrive LIGHTS UP. This is the tell,
    // and it is the single most important frame of feedback in the
    // game now that nothing is printed on the glass.
    if (glow.current) {
      const want = armed ? 0.25 + coil * 0.9 : (st.phase === "feint" ? 0.35 : 0);
      const m = glow.current.material;
      if (m) m.opacity += (want - m.opacity) * Math.min(1, dt * 12);
      const gy = 0.62 + (d ? d.ay * 0.46 : 0);
      glow.current.position.set(0, gy * tall, 0.3 + coil * 0.22);
      glow.current.scale.setScalar(0.75 + coil * 1.05);
    }

    // ── THE IDLE CHAIN ──
    // While the Haul is in its hand the chain hangs from the collar
    // ring to the pommel in a catenary, and sways. While the blade
    // is in the air the STAGE owns the chain and this half gets out
    // of the way entirely.
    if (isWarden) {
      const links = chainRefs.current;
      const show = holdWeapon && rise > 0.5;
      const a = v.a, b = v.b;
      // both endpoints in the body's LOCAL space — these links are
      // children of the same group, so no world maths is needed
      a.set(0.18, 1.36, 0.07);                              // the collar shackle
      b.set(0.3 + reach * 0.2, 0.34 - haul * 0.1, 0.52);    // the pommel
      for (let i = 0; i < links.length; i++) {
        const L = links[i];
        if (!L) continue;
        L.visible = show;
        if (!show) continue;
        const u = i / Math.max(1, links.length - 1);
        const sag = Math.sin(u * Math.PI) * (0.3 + Math.sin(t * 1.4) * 0.03);
        L.position.set(
          a.x + (b.x - a.x) * u + Math.sin(t * 1.1 + u * 4) * 0.012,
          a.y + (b.y - a.y) * u - sag,
          a.z + (b.z - a.z) * u + Math.cos(t * 0.9 + u * 3) * 0.012
        );
        L.rotation.x = Math.PI / 2 + Math.cos(u * Math.PI) * 0.5;
        L.rotation.z = u * 3.1;
      }
    }
  });

  return (
    <group ref={group}>
      {isWarden ? (
        <WardenRig refs={rig} foe={foe} char={char} eyeTex={eyeTex} weapon={weapon} />
      ) : isDisciple ? (
        <DiscipleRig refs={rig} foe={foe} char={char} eyeTex={eyeTex} weapon={weapon} />
      ) : isHound ? (
        <HoundRig refs={rig} foe={foe} char={char} eyeTex={eyeTex} big={big} />
      ) : (
        <HuskRig refs={rig} foe={foe} char={char} eyeTex={eyeTex} big={big} weapon={weapon} />
      )}

      {/* the idle chain lives OUTSIDE the torso group on purpose:
          the torso leans and twists hard, and a chain that leaned
          with it would read as welded rather than hung */}
      {isWarden && <ChainLinks refs={chainRefs} count={14} tint={foe.tint} scale={0.9} />}

      {/* ── THE TELL ── the limb that is about to arrive, lit.
          A sprite, additive, no light spent, driven imperatively
          off the coil. If a player can only see one thing in the
          frame, it should be this. */}
      <sprite ref={glow} position={[0, 0.62, 0.18]} scale={0.6}>
        <spriteMaterial
          map={billowTexture()} transparent depthWrite={false}
          blending={THREE.AdditiveBlending} toneMapped={false}
          opacity={0} color={foe.tint}
        />
      </sprite>
    </group>
  );
}
